import apiService from './api';
import logger from '@/utils/logger';
import { getUser } from '@/utils/storage';

/**
 * Service API spécialisé pour le tracking GPS et le suivi temps réel
 */
class TrackingApiService {
  /**
   * Récupérer toutes les positions GPS des bus
   */
  async getAllBusLocations() {
    try {
      // logger.debug('📍 Récupération positions bus via nouvelle API');

      // Utiliser la route publique temporaire pour tests
      const response = await apiService.get('/admin/tracking/bus-positions-public');

      // Gérer les deux formats possibles de réponse
      let positions = [];

      if (response.data && response.data.success) {
        // Format standard: {success: true, data: [...]}
        positions = response.data.data || [];
        // logger.debug(`✅ ${positions.length} position(s) récupérée(s) (format standard)`);
      } else if (Array.isArray(response.data)) {
        // Format direct: [{...}, {...}]
        positions = response.data;
        // logger.debug(`✅ ${positions.length} position(s) récupérée(s) (format direct)`);
      } else {
        logger.warn('⚠️ Format de réponse inattendu:', response.data);
        positions = [];
      }

      if (positions.length > 0) {
        // Transformer les données pour compatibilité avec l'interface
        // IMPORTANT: latitude/longitude doivent être à la racine pour InteractiveMap
        const busesWithLocations = positions.map((position) => ({
          id: position.bus_id || position.driver_id,
          bus_id: position.bus_id,
          bus_number: position.bus_number || `BUS-${position.driver_id?.slice(-3)}`,
          driver_name: position.driver_name || 'Chauffeur',
          route_name: position.route_name || 'Route inconnue',
          status: position.status,
          ignition: position.ignition,
          trip_type: position.trip_type,
          latitude: position.latitude,
          longitude: position.longitude,
          speed: position.speed || 0,
          heading: position.heading || 0,
          location: {
            latitude: position.latitude,
            longitude: position.longitude,
            speed: position.speed || 0,
            heading: position.heading || 0,
            accuracy: position.accuracy || 5,
            recorded_at: position.timestamp,
            stop_name: position.stop_name,
            source: 'simulation',
          },
          timestamp: position.timestamp,
          lastUpdate: position.timestamp,
          isOnline: true,
          isMoving: position.speed > 5,
          source: 'simulation',
        }));

        return busesWithLocations;
      } else {
        // logger.debug('📍 Aucune position GPS trouvée');
        return [];
      }
    } catch (error) {
      logger.error('❌ Erreur récupération positions bus:', error);

      // Fallback vers l'ancienne méthode si la nouvelle API échoue
      try {
        logger.debug('🔄 Fallback vers ancienne API...');
        const response = await apiService.get('/admin/buses');
        const buses = response.data.data;

        // Récupérer les positions GPS pour chaque bus
        const busesWithLocations = await Promise.all(
          buses.map(async (bus) => {
            try {
              const locationResponse = await apiService.get(`/admin/buses/${bus.id}/location`);
              return {
                ...bus,
                location: locationResponse.data.data,
              };
            } catch (error) {
              logger.warn(`Pas de position GPS pour le bus ${bus.id}:`, error.message);
              return {
                ...bus,
                location: null,
              };
            }
          }),
        );

        return busesWithLocations;
      } catch (fallbackError) {
        logger.error('❌ Erreur fallback:', fallbackError);
        throw new Error('Impossible de récupérer les positions des bus');
      }
    }
  }

  /**
   * Récupérer la position GPS d'un bus spécifique
   */
  async getBusLocation(busId) {
    try {
      const response = await apiService.get(`/admin/buses/${busId}/location`);
      return response.data.data;
    } catch (error) {
      logger.error(`Erreur lors de la récupération de la position du bus ${busId}:`, error);
      throw new Error(`Impossible de récupérer la position du bus ${busId}`);
    }
  }

  /**
   * Récupérer l'historique des positions d'un bus
   */
  async getBusLocationHistory(busId, options = {}) {
    try {
      const params = new URLSearchParams();

      if (options.startDate) {
        params.append('start_date', options.startDate);
      }
      if (options.endDate) {
        params.append('end_date', options.endDate);
      }
      if (options.limit) {
        params.append('limit', options.limit);
      }

      const response = await apiService.get(`/admin/buses/${busId}/location/history?${params}`);
      return response.data.data;
    } catch (error) {
      logger.error(`Erreur lors de la récupération de l'historique du bus ${busId}:`, error);
      throw new Error(`Impossible de récupérer l'historique du bus ${busId}`);
    }
  }

  /**
   * Récupérer tous les circuits avec leurs arrêts
   */
  async getRoutesWithStops() {
    try {
      logger.debug('🛣️ Récupération routes et arrêts via nouvelles APIs...');

      // Récupérer school_id depuis le localStorage (admin connecté)
      const userStr = getUser();
      let schoolId = null;

      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          schoolId = user.school_id || 'f47ac10b-58cc-4372-a567-0e02b2c3d479'; // École Al Massira par défaut
          logger.debug('🏫 School ID admin:', schoolId);
        } catch {
          logger.warn('⚠️ Erreur parsing user, utilisation école par défaut');
          schoolId = 'f47ac10b-58cc-4372-a567-0e02b2c3d479'; // École Al Massira
        }
      } else {
        logger.warn('⚠️ Aucun user trouvé, utilisation école par défaut');
        schoolId = 'f47ac10b-58cc-4372-a567-0e02b2c3d479'; // École Al Massira
      }

      // Récupérer routes, arrêts et écoles en parallèle via APIs publiques avec filtrage
      const [routesResponse, stopsResponse, schoolsResponse] = await Promise.all([
        apiService.get(`/admin/routes-public?school_id=${schoolId}`).catch(() => ({ data: [] })),
        apiService.get(`/admin/routes-public/stops?school_id=${schoolId}`).catch(() => ({ data: [] })),
        apiService.get(`/admin/routes-public/schools?school_id=${schoolId}`).catch(() => ({ data: [] })),
      ]);

      logger.debug('📊 Réponses APIs publiques (école filtrée):', {
        routes: routesResponse?.data?.length || 0,
        stops: stopsResponse?.data?.length || 0,
        schools: schoolsResponse?.data?.length || 0,
        schoolId: schoolId,
      });

      // Traitement des routes
      const routes = Array.isArray(routesResponse.data) ? routesResponse.data : routesResponse?.data?.data || [];

      // Traitement des arrêts
      const stops = Array.isArray(stopsResponse.data) ? stopsResponse.data : stopsResponse?.data?.data || [];

      // Traitement des écoles
      const schools = Array.isArray(schoolsResponse.data) ? schoolsResponse.data : schoolsResponse?.data?.data || [];

      logger.debug('✅ Données traitées (école filtrée):', {
        routes: routes.length,
        stops: stops.length,
        schools: schools.length,
      });

      return {
        routes,
        stops,
        schools,
      };
    } catch (error) {
      logger.error('❌ Erreur récupération routes/arrêts:', error);

      // Fallback : essayer de récupérer depuis les positions bus
      try {
        logger.debug('🔄 Fallback : extraction depuis positions bus...');
        const positions = await this.getAllBusLocations();

        if (positions && positions.length > 0) {
          logger.debug(`🚏 Extraction arrêts depuis ${positions.length} position(s)...`);

          const extractedStops = [];
          const extractedRoutes = [];
          const routeIds = new Set();

          positions.forEach((position) => {
            // Extraire arrêt si disponible
            if (position.stop_name && position.latitude && position.longitude) {
              logger.debug('🚏 Arrêt extrait:', position.stop_name);
              extractedStops.push({
                id: position.stop_id || `stop-${extractedStops.length}`,
                name: position.stop_name,
                latitude: position.latitude,
                longitude: position.longitude,
                routeId: position.route_id,
              });
            }

            // Extraire route si disponible
            if (position.route_id && !routeIds.has(position.route_id)) {
              routeIds.add(position.route_id);
              extractedRoutes.push({
                id: position.route_id,
                name: position.route_name || 'Circuit',
                description: 'Circuit extrait depuis GPS',
              });
            }
          });

          logger.debug(`✅ Extraction terminée: ${extractedStops.length} arrêts, ${extractedRoutes.length} routes`);

          return {
            routes: extractedRoutes,
            stops: extractedStops,
            schools: [],
          };
        }
      } catch (fallbackError) {
        logger.error('❌ Erreur fallback:', fallbackError);
      }

      // Dernier fallback : données vides
      return {
        routes: [],
        stops: [],
        schools: [],
      };
    }
  }

  /**
   * Récupérer les statistiques de tracking
   */
  async getTrackingStats() {
    try {
      const response = await apiService.get('/admin/tracking/stats');
      return response.data.data;
    } catch {
      // Fallback avec calcul côté client si l'endpoint n'existe pas
      logger.warn('Endpoint stats non disponible, calcul côté client');

      try {
        // Récupérer les bus avec GPS actif
        const busesWithGPS = await this.getAllBusLocations();
        // logger.debug('📊 Bus avec GPS actif:', busesWithGPS.length);

        // Récupérer le total de bus depuis la BDD
        let totalBuses = busesWithGPS.length;
        try {
          const response = await apiService.get('/admin/tracking/bus-positions-public');
          if (response.data?.success && response.data?.data) {
            totalBuses = Math.max(totalBuses, response.data.data.length);
          }
        } catch {
          logger.warn('⚠️ Impossible de récupérer le total de bus, utilisation GPS uniquement');
        }

        // Calculer vitesse moyenne
        const activeBuses = busesWithGPS.filter((bus) => this.isActive(bus));
        const totalSpeed = activeBuses.reduce((sum, bus) => sum + (bus.location?.speed || 0), 0);
        const averageSpeed = activeBuses.length > 0 ? Math.round(totalSpeed / activeBuses.length) : 0;

        // Compter les étudiants (estimation basée sur les bus actifs)
        const studentsCount = busesWithGPS.length > 0 ? 18 : 0; // 18 élèves dans notre jeu de test

        // 🌅 NOUVEAU: Détecter le type de trajet actif
        let tripType = null;
        if (busesWithGPS.length > 0) {
          // Chercher le premier bus actif pour déterminer le type de trajet
          const activeBus = busesWithGPS.find((bus) => this.isActive(bus));
          if (activeBus && activeBus.trip_type) {
            tripType = activeBus.trip_type;
          }
        }

        const stats = {
          total: totalBuses, // Total de bus en BDD
          active: busesWithGPS.filter((bus) => this.isActive(bus)).length,
          stopped: busesWithGPS.filter((bus) => this.isStopped(bus)).length,
          tripType, // 🌅 Nouveau: type de trajet actif (morning/evening/null)
          delayed: busesWithGPS.filter((bus) => this.isDelayed(bus)).length,
          maintenance: busesWithGPS.filter((bus) => bus.status === 'maintenance').length,
          offline: totalBuses - busesWithGPS.length, // Bus sans GPS = hors ligne
          mobile: busesWithGPS.filter((bus) => bus.location?.source === 'simulation').length,
          simulated: busesWithGPS.filter((bus) => bus.location?.source === 'simulation').length,
          students: studentsCount,
          averageSpeed: averageSpeed,
        };

        // logger.debug('📊 Stats calculées:', stats);
        return stats;
      } catch (fallbackError) {
        logger.error('Erreur lors du calcul des statistiques:', fallbackError);
        throw new Error('Impossible de calculer les statistiques');
      }
    }
  }

  /**
   * Récupérer les alertes actives
   */
  async getActiveAlerts() {
    try {
      const response = await apiService.get('/admin/alerts/active');
      return response.data.data;
    } catch (error) {
      logger.warn('Endpoint alertes non disponible:', error.message);
      return [];
    }
  }

  /**
   * Créer une alerte d'urgence
   */
  async createEmergencyAlert(alertData) {
    try {
      const response = await apiService.post('/admin/alerts/emergency', alertData);
      return response.data.data;
    } catch (error) {
      logger.error("Erreur lors de la création de l'alerte:", error);
      throw new Error("Impossible de créer l'alerte d'urgence");
    }
  }

  /**
   * Rechercher des bus par critères
   */
  async searchBuses(criteria) {
    try {
      const params = new URLSearchParams();

      if (criteria.search) {
        params.append('search', criteria.search);
      }
      if (criteria.status && criteria.status !== 'all') {
        params.append('status', criteria.status);
      }
      if (criteria.route && criteria.route !== 'all') {
        params.append('route_id', criteria.route);
      }

      const response = await apiService.get(`/admin/buses/search?${params}`);
      return response.data.data;
    } catch (error) {
      logger.error('Erreur lors de la recherche de bus:', error);
      throw new Error('Impossible de rechercher les bus');
    }
  }

  // Méthodes utilitaires pour déterminer le statut des bus

  /**
   * Détermine si un bus est actif (en mouvement)
   */
  isActive(bus) {
    if (!bus.location) return false;
    const speed = bus.location.speed || 0;
    return speed > 5; // Plus de 5 km/h = en mouvement
  }

  /**
   * Détermine si un bus est arrêté
   */
  isStopped(bus) {
    if (!bus.location) return true;
    const speed = bus.location.speed || 0;
    return speed <= 5; // 5 km/h ou moins = arrêté
  }

  /**
   * Détermine si un bus est en retard
   */
  isDelayed(bus) {
    // Logique de retard basée sur l'horaire prévu vs position actuelle
    // Pour l'instant, utiliser le statut explicite
    return bus.status === 'delayed';
  }

  /**
   * Détermine si un bus est hors ligne
   */
  isOffline(bus) {
    if (!bus.location || !bus.location.recorded_at) return true;

    const lastUpdate = new Date(bus.location.recorded_at);
    const now = new Date();
    const minutesSinceUpdate = (now - lastUpdate) / (1000 * 60);

    return minutesSinceUpdate > 10; // Plus de 10 minutes = hors ligne
  }

  /**
   * Calculer la distance entre deux points GPS
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Rayon de la Terre en km
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance en km
  }

  /**
   * Convertir degrés en radians
   */
  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  /**
   * Formater une position GPS pour l'affichage
   */
  formatPosition(latitude, longitude) {
    return {
      latitude: parseFloat(latitude).toFixed(6),
      longitude: parseFloat(longitude).toFixed(6),
      display: `${parseFloat(latitude).toFixed(4)}, ${parseFloat(longitude).toFixed(4)}`,
    };
  }

  /**
   * Calculer l'ETA (temps d'arrivée estimé) pour un bus
   */
  calculateETA(bus, targetStop) {
    if (!bus.location || !targetStop) return null;

    const distance = this.calculateDistance(
      bus.location.latitude,
      bus.location.longitude,
      targetStop.latitude,
      targetStop.longitude,
    );

    const speed = bus.location.speed || 30; // Vitesse par défaut 30 km/h
    const timeInHours = distance / speed;
    const timeInMinutes = Math.round(timeInHours * 60);

    return {
      distance: distance.toFixed(2),
      timeMinutes: timeInMinutes,
      eta: new Date(Date.now() + timeInMinutes * 60000).toLocaleTimeString(),
    };
  }
}

// Instance singleton
const trackingApi = new TrackingApiService();

export default trackingApi;
